<template>
  <section
    class="card"
    role="region"
    aria-labelledby="login-devices-title"
  >
    <form class="form mt-12">
      <div class="field">
        <label for="date-from">Datum von</label>
        <flat-pickr
          id="date-from"
          v-model="filters.dateFrom"
          :config="dateCfg"
          @on-change="onDatesChanged"
        />
      </div>
      <div class="field">
        <label for="date-to">Datum bis</label>
        <flat-pickr
          id="date-to"
          v-model="filters.dateTo"
          :config="dateCfg"
          @on-change="onDatesChanged"
        />
      </div>
      <div
        class="field"
        style="min-width:160px;"
      >
        <label for="role-filter">Rolle</label>
        <select
          id="role-filter"
          v-model="roleFilter"
          @change="onRoleChanged"
        >
          <option value="">
            Alle
          </option>
          <option value="Eltern">
            Eltern
          </option>
          <option value="Lehrer">
            Lehrer
          </option>
          <option value="Admin">
            Admin
          </option>
        </select>
      </div>
    </form>
    <p
      v-if="error"
      class="notice notice-danger mt-12"
      role="alert"
    >
      {{ error }}
    </p>

    <!-- Viewport-Zusammenfassung (Kacheln) -->
    <div
      v-if="rows.length"
      class="summary-tiles mt-12 mb-5"
      aria-label="Geräte (unique, nach Breite)"
    >
      <div class="summary-tile">
        <i
          class="pi pi-mobile icon"
          aria-hidden="true"
        />
        <div class="meta">
          <div class="label">
            Phone
          </div>
          <div class="sub muted">
            bis 799px
          </div>
        </div>
        <div
          class="count"
          aria-label="Phone Geräte"
        >
          {{ viewportSummary.phone }}
        </div>
      </div>

      <div class="summary-tile">
        <i
          class="pi pi-tablet icon"
          aria-hidden="true"
        />
        <div class="meta">
          <div class="label">
            Pad
          </div>
          <div class="sub muted">
            800–1199px
          </div>
        </div>
        <div
          class="count"
          aria-label="Pad Geräte"
        >
          {{ viewportSummary.pad }}
        </div>
      </div>

      <div class="summary-tile">
        <i
          class="pi pi-desktop icon"
          aria-hidden="true"
        />
        <div class="meta">
          <div class="label">
            Desktop
          </div>
          <div class="sub muted">
            ab 1200px
          </div>
        </div>
        <div
          class="count"
          aria-label="Desktop Geräte"
        >
          {{ viewportSummary.desktop }}
        </div>
      </div>
    </div>


    <div
      v-if="rows.length"
      class="table mt-12"
    >
      <div class="thead">
        <div class="th">
          Login (Username)
        </div>
        <div class="th">
          Rolle
        </div>
        <div class="th">
          Geräte / Auflösungen / Orientation
        </div>
      </div>
      <div class="tbody">
        <div
          v-for="row in pagedRows"
          :key="row.username"
          class="tr"
        >
          <div class="td">
            <div class="body">
              {{ row.username }}
            </div>
            <div
              v-if="row.userEmail"
              class="meta muted"
            >
              {{ row.userEmail }}
            </div>
          </div>
          <div class="td">
            {{ row.role || '—' }}
          </div>
          <div class="td">
            <div class="chips">
              <span
                v-for="d in row.devices"
                :key="d.key"
                class="chip"
                :title="d.userAgent"
              >
                {{ d.label }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination wie bei Gruppen -->
      <div
        class="mt-12 pagination-bar"
      >
        <span
          class="muted"
          style="margin-right:auto"
        >Gesamt: {{ filteredRows.length }}</span>
        <label
          class="muted"
          for="login-devices-limit"
        >pro Seite:</label>
        <select
          id="login-devices-limit"
          :value="limit"
          @change="changeLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prevPage"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="nextPage"
        >
          Weiter
        </button>
      </div>
    </div>
  </section>
</template>
<script setup>
import {ref, computed, onMounted} from 'vue'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import api from '@/services/api';

function todayIso() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const dateCfg = {
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'd.m.Y',
  locale: 'de',
  time_24hr: true,
}

const loading = ref(false)
const error = ref('')

// Von/Bis standardmäßig auf heute vorbelegen
const rows = ref([])
const filters = ref({
  dateFrom: todayIso(),
  dateTo: todayIso(),
})

// Filter: Rolle
const roleFilter = ref('')
const filteredRows = computed(() => {
  const rf = String(roleFilter.value || '')
  if (!rf) return rows.value
  if (rf === '__none') return rows.value.filter(r => !r.role)
  return rows.value.filter(r => String(r.role || '').toLowerCase() === rf.toLowerCase())
})

// Pagination (lokal, wie bei Gruppen)
const page = ref(1)
const limit = ref(20)

const pages = computed(() => {
  const total = filteredRows.value.length
  return total > 0 ? Math.ceil(total / limit.value) : 1
})

const pagedRows = computed(() => {
  const start = (page.value - 1) * limit.value
  return filteredRows.value.slice(start, start + limit.value)
})

// Viewport-Zusammenfassung (unique Device-Signatures nach Breite)
const viewportSummary = computed(() => {
  const seen = new Set()
  const result = {
    phone: 0,
    pad: 0,
    desktop: 0,
  }
  for (const row of filteredRows.value) {
    if (!Array.isArray(row.devices)) continue
    for (const d of row.devices) {
      const key = d.key || ''
      if (!key || seen.has(key)) continue
      seen.add(key)
      // key-Struktur: name|dpr|WIDTHxHEIGHT|orientation
      const parts = String(key).split('|')
      const wh = parts[2] || ''
      const widthStr = wh.split('x')[0] || ''
      const width = parseInt(widthStr, 10) || 0
      let bucket = 'desktop'
      if (width > 0 && width < 800) {
        bucket = 'phone'
      } else if (width >= 800 && width < 1200) {
        bucket = 'pad'
      } else if (width > 0 && width >= 1200) {
        bucket = 'desktop'
      }
      result[bucket]++
    }
  }
  return result
})

function onDatesChanged() {
  page.value = 1
  load()
}

function onRoleChanged() {
  page.value = 1
}

async function load() {
  loading.value = true
  error.value = ''
  rows.value = []
  try {
    const res = await api.get('/admin/login-devices/analytics', {
      params: {
        dateFrom: filters.value.dateFrom,
        dateTo: filters.value.dateTo,
      },
    })
    const data = res?.data
    rows.value = Array.isArray(data?.data) ? data.data : []
    page.value = 1
  } catch (e) {
    error.value = e?.message || String(e)
  } finally {
    loading.value = false
  }
}

// Pagination-Handler
function changeLimit(e) {
  limit.value = Number(e.target.value) || 20
  page.value = 1
}

function nextPage() {
  if (page.value < pages.value) {
    page.value++
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value--
  }
}

onMounted(() => {
  load()
})
</script>

<style scoped>
.h2 {
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  margin: 0;
}

.meta {
  font-size: 12px;
  line-height: 16px;
}

.mt-12 {
  margin-top: 12px;
}

.form {
  display: grid;
  grid-template-columns: 110px 110px 200px;
  gap: 12px;
  align-items: end;
}

.field label {
  font-size: 12px;
  color: var(--muted, #6B7280);
  display: block;
  margin-bottom: 4px;
}

.actions {
  display: flex;
  gap: 8px;
}

.btn {
  height: 38px;
  padding: 0 14px;
  border: 1px solid var(--border, #E0E3EA);
  border-radius: 8px;
  background: var(--surface, #fff);
  color: var(--text, #1F2937);
  cursor: pointer;
}

.btn-primary {
  background: var(--primary, #4F46E5);
  color: #fff;
  border-color: var(--primary, #4F46E5);
}

.btn-primary:hover {
  background: var(--primary-600, #4338CA);
}

.btn:disabled {
  opacity: .65;
  cursor: default;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, .6);
  border-top-color: rgba(255, 255, 255, 1);
  margin-right: 8px;
  animation: spin .8s linear infinite;
  vertical-align: -3px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.notice {
  border: 1px solid var(--border, #E0E3EA);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface, #fff);
}

.notice-danger {
  background: rgba(239, 68, 68, 0.08);
  border-color: #FCA5A5;
}

.table {
  display: grid;
  gap: 0;
}

.thead, .tr {
  display: grid;
  grid-template-columns: 240px 120px 1fr;
  border-bottom: 1px solid var(--border, #E0E3EA);
  align-items: center;
}

.th {
  padding: 8px 10px;
  font-size: 12px;
  color: var(--muted, #6B7280);
}

.td {
  padding: 3px 10px;
  font-size: 14px;
  line-height: 20px;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  display: inline-block;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 999px;
  background: rgba(79, 70, 229, .08);
  color: var(--text, #1F2937);
  border: 1px solid var(--border, #E0E3EA);
}

@media (max-width: 768px) {
  .thead, .tr {
    grid-template-columns: 1fr;
  }
}

.summary-tiles {
  display: grid;
  grid-template-columns: repeat(3, 250px);
  gap: 12px;
}

.summary-tile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--border, #E0E3EA);
  border-radius: 10px;
  background: var(--surface, #FFFFFF);
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
  transition: background .16s ease, border-color .16s ease, transform .06s ease;
}

.summary-tile:hover {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
  border-color: rgba(79, 70, 229, .35);
}

.summary-tile .icon {
  font-size: 20px;
  color: var(--primary, #4F46E5);
}

.summary-tile .meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.summary-tile .label {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted, #6B7280);
}

.summary-tile .sub {
  font-size: 12px;
  line-height: 16px;
}

.summary-tile .count {
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  min-width: 28px;
  text-align: right;
}

.pagination-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
}
</style>